import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  DeleteGlobalDocumentPayload,
  GetSupplierDocumentsResponsePayload,
  GlobalDocument,
  InsertGlobalDocumentPayload,
  ListGlobalDocumentsResponsePayload,
  UpdateGlobalDocumentPayload,
  documentsTopLevelFilterKeys,
} from '@omnea-app/documents/documentsFrontendClient'
import { DocumentsHttpClient } from '@omnea-app/documents/documentsHttpClient'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { recordAuthorizationQueryKey } from '@omnea-app/fe-authz/recordAuthorizationQuery'
import { replaceObjectInlineInArray } from '@omnea-app/fe-legacy/utils/general/replaceObjectInlineInArray'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { classificationRefetchInterval } from './classificationRefetchInterval'

export const GetAllGlobalDocumentsQueryKey = 'GetAllDocuments'
export const GetSupplierDocumentsQueryKey = 'GetSupplierDocuments'

type ListedDocument = ListGlobalDocumentsResponsePayload['documents'][number]

// A write returns the document without its inherited confidentiality, which only the lists compute. The row keeps what
// its list last read, and a new one shows none until the list refetches.
const toListedDocument = (document: GlobalDocument, listed?: Pick<ListedDocument, 'inheritedFrom'>) => ({
  ...document,
  inheritedFrom: listed?.inheritedFrom ?? [],
})

export const useGetSupplierDocuments = (organisationSupplierId: string) => {
  const { getToken } = useAuthenticatedUser()
  return useQuery({
    queryKey: [GetSupplierDocumentsQueryKey, organisationSupplierId],
    queryFn: async () => {
      const token = await getToken()
      const documentsClient = new DocumentsHttpClient({ authorization: token })
      const { data, error } = await documentsClient.GET('/supplier/{organisationSupplierId}/documents', {
        params: { path: { organisationSupplierId } },
      })
      throwIfApiError(error)
      return data
    },
    refetchInterval: (query) => classificationRefetchInterval(query.state.data),
  })
}

export const useInsertGlobalDocument = () => {
  const queryClient = useQueryClient()
  const { getToken } = useAuthenticatedUser()
  return useMutation({
    mutationFn: async ({
      organisationSupplierId,
      organisationProductId,
      type,
      fileName,
      filestoreId,
      agreementId,
    }: InsertGlobalDocumentPayload) => {
      const token = await getToken()
      const documentsClient = new DocumentsHttpClient({ authorization: token })
      const { data, error } = await documentsClient.POST('/global/insert', {
        body: { organisationSupplierId, organisationProductId, type, fileName, filestoreId, agreementId },
      })
      throwIfApiError(error)
      return data
    },
    onSuccess: (successData) => {
      queryClient.setQueriesData(
        { queryKey: [GetAllGlobalDocumentsQueryKey] },
        (oldData: ListGlobalDocumentsResponsePayload['documents'] | undefined) => {
          if (!Array.isArray(oldData)) {
            return oldData
          }
          return [toListedDocument(successData), ...oldData]
        }
      )
      if (successData?.supplier?.id) {
        queryClient.setQueryData(
          [GetSupplierDocumentsQueryKey, successData.supplier.id],
          (oldData: GetSupplierDocumentsResponsePayload) => {
            if (!Array.isArray(oldData)) {
              return [toListedDocument(successData)]
            }
            return [toListedDocument(successData), ...oldData]
          }
        )
      }
    },
  })
}

export const useUpdateGlobalDocument = () => {
  const queryClient = useQueryClient()
  const { getToken, user } = useAuthenticatedUser()
  return useMutation({
    mutationFn: async (payload: UpdateGlobalDocumentPayload) => {
      const token = await getToken()
      const documentsClient = new DocumentsHttpClient({ authorization: token })
      const { data, error } = await documentsClient.POST('/global/update', { body: payload })
      throwIfApiError(error)
      return data
    },
    onSuccess: (successData, payload) => {
      // A move changes the document's parents, and so what the caller may do to it.
      void queryClient.invalidateQueries({ queryKey: recordAuthorizationQueryKey(user.id, 'document', payload.id) })
      queryClient.setQueriesData(
        { queryKey: [GetAllGlobalDocumentsQueryKey, 'all'] },
        (oldData?: ListGlobalDocumentsResponsePayload['documents']) =>
          replaceObjectInlineInArray(
            oldData,
            toListedDocument(
              successData,
              oldData?.find((doc) => doc.id === successData.id)
            )
          )
      )
      if (successData?.supplier?.id) {
        queryClient.setQueryData(
          [GetSupplierDocumentsQueryKey, successData.supplier.id],
          (oldData: GetSupplierDocumentsResponsePayload) => {
            if (!Array.isArray(oldData)) {
              return [toListedDocument(successData)]
            }
            const listed = oldData.find((doc) => doc.id === successData.id)
            return [...oldData.filter((doc) => doc.id !== successData?.id), toListedDocument(successData, listed)]
          }
        )
      }
    },
  })
}

export const useDeleteGlobalDocument = () => {
  const queryClient = useQueryClient()
  const { getToken } = useAuthenticatedUser()
  return useMutation({
    mutationFn: async ({ id }: DeleteGlobalDocumentPayload) => {
      const token = await getToken()
      const documentsClient = new DocumentsHttpClient({ authorization: token })
      const { data, error } = await documentsClient.POST('/global/delete', {
        body: { id },
      })
      throwIfApiError(error)
      return data
    },
    onSuccess: (successData) => {
      const deletedDocId = successData?.id
      if (deletedDocId) {
        documentsTopLevelFilterKeys.forEach((filterKey) => {
          queryClient.setQueryData(
            [GetAllGlobalDocumentsQueryKey, filterKey],
            (oldData: ListGlobalDocumentsResponsePayload['documents'] | undefined) => {
              // Check if oldData is actually an array before attempting to filter
              if (!Array.isArray(oldData)) {
                return oldData // Return non-array data as is
              }
              // Now oldData is confirmed to be an array, safely filter
              return oldData.filter((doc) => doc.id !== deletedDocId)
            }
          )
        })
      }
    },
  })
}
